import { DeleteIcon, EditAltIcon, RmxCheckIcon } from 'cokitchen-icons';
import React from 'react';
import { Table, TableData, TableRow } from '@cokitchen/cokitchen-components';
import SelectDropdown from 'components/base/select-dropdown';
import EditableInput from 'components/base/editable-input';
import useVendorContext from 'contexts/vendor.context';

type VendorInfoTabProps = {
  editingVendorIndex: number | null;
  onVendorInputChange: (index: number, field: string, value: string) => void;
  onEditVendor: (index: number) => void;
  onSaveVendor: () => void;
  onDeleteVendor: (index: number) => void;
};

const VendorInfoTab: React.FC<VendorInfoTabProps> = ({
  editingVendorIndex,
  onVendorInputChange,
  onEditVendor,
  onSaveVendor,
  onDeleteVendor
}) => {
  const { vendorFormData, bankList } = useVendorContext();

  const VENDOR_DETAILS_HEADERS = [
    'Name',
    'Phone Number',
    'Bank Name',
    'Account Number',
    'Account Name',
    'Payment Period',
    'Actions'
  ];

  const isVendorEditing = (index: number): boolean => {
    return editingVendorIndex === index;
  };

  return (
    <Table headers={VENDOR_DETAILS_HEADERS}>
      {vendorFormData.length === 0
        ? [
            <TableRow key='empty'>
              <TableData className='text-center py-8 text-gray-400'>No vendors added yet</TableData>
            </TableRow>
          ]
        : vendorFormData.map((vendor, vendorIndex) => {
            const isEditing = isVendorEditing(vendorIndex);

            return (
              <TableRow key={vendorIndex} className='hover:bg-gray-50 transition-colors'>
                <TableData className='px-2 py-2 text-sm text-gray-800'>
                  <EditableInput
                    isEditing={isEditing}
                    value={vendor.name}
                    showError
                    type='text'
                    onChange={(e) => onVendorInputChange(vendorIndex, 'name', e.target.value)}
                  />
                </TableData>

                <TableData className='px-2 py-2 text-sm text-gray-800'>
                  <EditableInput
                    isEditing={isEditing}
                    value={vendor.phone_number}
                    showError
                    type='tel'
                    onChange={(e) =>
                      onVendorInputChange(vendorIndex, 'phone_number', e.target.value)
                    }
                  />
                </TableData>

                <TableData className='px-2 py-2 text-sm text-gray-800'>
                  {isEditing ? (
                    <SelectDropdown
                      hasSearch
                      placeholder='Select bank'
                      value={vendor.bank_name}
                      setValue={(bank) => onVendorInputChange(vendorIndex, 'bank_name', bank.name)}
                      options={bankList}
                    />
                  ) : (
                    <span>{vendor.bank_name}</span>
                  )}
                </TableData>

                <TableData className='px-2 py-2 text-sm text-gray-800'>
                  <EditableInput
                    isEditing={isEditing}
                    value={vendor.account_number}
                    showError
                    type='number'
                    onChange={(e) =>
                      onVendorInputChange(vendorIndex, 'account_number', e.target.value)
                    }
                  />
                </TableData>

                <TableData className='px-2 py-2 text-sm text-gray-800'>
                  <EditableInput
                    isEditing={isEditing}
                    showError
                    value={vendor.account_name}
                    type='text'
                    onChange={(e) =>
                      onVendorInputChange(vendorIndex, 'account_name', e.target.value)
                    }
                  />
                </TableData>

                <TableData className='px-2 py-2 text-sm text-gray-800'>
                  <EditableInput
                    isEditing={isEditing}
                    value={vendor.payment_period}
                    showError
                    type='text'
                    onChange={(e) =>
                      onVendorInputChange(vendorIndex, 'payment_period', e.target.value)
                    }
                  />
                </TableData>

                <TableData className='px-2 py-2 text-sm'>
                  <div className='flex items-center gap-2'>
                    {isEditing ? (
                      <div className='cursor-pointer text-green-6' onClick={onSaveVendor}>
                        <RmxCheckIcon />
                      </div>
                    ) : (
                      <div
                        className='cursor-pointer text-blue-5'
                        onClick={() => onEditVendor(vendorIndex)}>
                        <EditAltIcon />
                      </div>
                    )}
                    <div
                      className='cursor-pointer text-red-5'
                      onClick={() => onDeleteVendor(vendorIndex)}>
                      <DeleteIcon />
                    </div>
                  </div>
                </TableData>
              </TableRow>
            );
          })}
    </Table>
  );
};

export default VendorInfoTab;
